import { useState, useCallback } from 'react';
import api from '../../../services/api';
import { retryWithBackoff } from '../chat-utils';

const chatAPI = {
  sendMessage: (data) => api.post('/chat/messages', data),
  getSessions: () => api.get('/chat/sessions'),
  getMessages: (sessionId) => api.get(`/chat/sessions/${sessionId}/messages`),
  deleteSession: (sessionId) => api.delete(`/chat/sessions/${sessionId}`),
  sendFeedback: (messageId, feedback) => api.post(`/chat/messages/${messageId}/feedback`, feedback),
};

/**
 * Hook for managing chat sessions and messages
 * Handles session CRUD, message persistence, and error recovery
 */
export function useChatSession() {
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sendMessage = useCallback(async (content, sessionId = null) => {
    setLoading(true);
    setError(null);

    const optimisticUser = {
      id: `tmp-${Date.now()}`,
      session_id: sessionId,
      role: 'user',
      content,
      sources: [],
      requires_professional_advice: false,
      confidence: 'user',
      suggested_questions: [],
      created_at: new Date().toISOString(),
    };

    setMessages(prev => [...prev, optimisticUser]);

    try {
      const res = await retryWithBackoff(() =>
        chatAPI.sendMessage({ content, session_id: sessionId || undefined })
      );

      const data = res.data;

      if (!activeSessionId && data.session_id) {
        setActiveSessionId(data.session_id);
      }

      setMessages(prev => {
        const without = prev.filter(m => m.id !== optimisticUser.id);
        // Ensure both messages are valid before adding
        const newMessages = [
          ...(data.user_message ? [data.user_message] : []),
          ...(data.assistant_message ? [data.assistant_message] : []),
        ];
        return [...without, ...newMessages];
      });

      return data.assistant_message;
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== optimisticUser.id));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [activeSessionId]);

  const loadSessions = useCallback(async () => {
    try {
      const data = await retryWithBackoff(() => chatAPI.getSessions());
      setSessions(data);
    } catch (err) {
      setError(err);
    }
  }, []);

  const loadSession = useCallback(async (sessionId) => {
    setActiveSessionId(sessionId);
    setMessages([]);
    setError(null);

    try {
      const data = await retryWithBackoff(() => chatAPI.getMessages(sessionId));
      setMessages(data);
    } catch (err) {
      setError(err);
      setMessages([]);
    }
  }, []);

  const deleteSession = useCallback(async (sessionId) => {
    try {
      await retryWithBackoff(() => chatAPI.deleteSession(sessionId));
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
        setMessages([]);
      }
    } catch (err) {
      setError(err);
    }
  }, [activeSessionId]);

  const sendFeedback = useCallback(async (messageId, feedback) => {
    try {
      await retryWithBackoff(() => chatAPI.sendFeedback(messageId, feedback));
      // Update local message to reflect feedback was sent
      setMessages(prev =>
        prev.map(m => m.id === messageId ? { ...m, feedback_sent: true } : m)
      );
    } catch (err) {
      // Don't block UI on feedback error
      console.error('Failed to send feedback:', err);
    }
  }, []);

  const startNewChat = useCallback(() => {
    setActiveSessionId(null);
    setMessages([]);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    sessions,
    activeSessionId,
    messages,
    loading,
    error,

    // Actions
    sendMessage,
    loadSessions,
    loadSession,
    deleteSession,
    sendFeedback,
    startNewChat,
    clearError,

    // Computed
    isEmpty: messages.length === 0 && !loading,
  };
}
